"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { parseAsString, useQueryState } from "nuqs";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";
import { PipelineTable } from "./table";
import { StageBar } from "./table/stage-bar";
import { PipelineToolbar } from "./table/toolbar";
import { PipelineBulkActions } from "./bulk-actions";
import type { PipelineCandidate } from "@/lib/api/types";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { bulkMove, bulkArchive } from "@/lib/api/generated";
import {
  buildPipelineActionBody,
  type PipelineActionBody,
} from "@/features/jobs/store/pipeline/selection";
import { SyncFlowProvider } from "@/features/ats-sync/components/sync/use-sync-flow";
import { invalidateSyncPreflight } from "@/features/ats-sync/lib/invalidate-after-sync";
import { RejectInAtsModal } from "@/features/ats-sync/components/reject-in-ats-modal";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { rejectAvailability } from "@/features/ats-sync/lib/gating";
import { AtsToolbarAction } from "@/features/ats-sync/components/ats-toolbar-action";
import { patchSyncedCandidatesQueued } from "@/features/jobs/lib/optimistic-queued-patch";
import { getArchiveStage } from "./archive-stage";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import { showToast } from "@/components/ui/sonner";
import { PipelineProfile } from "./profile";
import { NeedsStageTray } from "./needs-stage-tray";
import { PipelineViewOptions } from "./view-options";
import { MobilePipelineCards } from "./mobile-cards";
import { PipelineEmptyState } from "./empty-state";
import {
  BulkMoveProgressToast,
  type BulkMoveProgressSession,
} from "./bulk-move-progress-toast";
import {
  ExportModal,
  type ExportOptions,
} from "@/features/search/components/actions/export";
import {
  profileExportAdapter,
  profileExportLabel,
  useExportBusy,
  useStartExportJob,
} from "@/features/exports/tracker";
import {
  requestExport,
  type ExportFormat,
  type ExportSource,
} from "@/hooks/use-search";
import { useAutoStartTour } from "@/features/tours";
import { descriptionForPipelineBulkError } from "@/features/jobs/components/view/pipeline/bulk-selection-limit";
import {
  PipelineFilterButton,
  PipelineFilterSidebar,
} from "./filters";
import { pipelineExportScope } from "./export-jobs";

interface PipelineViewProps {
  candidates: PipelineCandidate[];
  totalCount: number;
  loading?: boolean;
  onOpenImport?: () => void;
  /**
   * URL-derived filter/search that produced this list. Read from the same
   * values as the pipeline query so an empty result after `ensureJobScope`
   * (which wipes the store mirror) still shows "No matching profiles"
   * instead of the unfiltered add-profiles empty state.
   */
  hasActiveFilters?: boolean;
}

export function PipelineView(props: PipelineViewProps) {
  const { job } = usePipelineJob();
  return (
    <SyncFlowProvider resumeJobId={job.id}>
      <PipelineViewInner {...props} />
    </SyncFlowProvider>
  );
}

function PipelineViewInner({
  candidates,
  totalCount,
  loading,
  onOpenImport,
  hasActiveFilters = false,
}: PipelineViewProps) {
  const { job, stages, onRefresh } = usePipelineJob();
  const {
    selectedStageId,
    selectAllCandidates,
    clearSelection,
    filterSearch,
    getSelectedCount,
    bulkMode,
    selectedCandidateIds,
  } = usePipelineViewStore();
  const hasSelection = getSelectedCount() > 0;

  const filters = usePipelineViewStore((s) => s.filters);

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    null,
  );
  // Deep link from Cmd+K (and shareable URLs): `?profile=<profile_id>` opens
  // the candidate sheet once, then is stripped so the sheet's own navigation
  // doesn't fight the URL.
  const [profileParam, setProfileParam] = useQueryState(
    "profile",
    parseAsString.withOptions({
      history: "replace",
      scroll: false,
      shallow: true,
      clearOnDefault: true,
    }),
  );
  useEffect(() => {
    if (!profileParam) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot URL param consumption; runs only when a ?profile= deep link arrives
    setSelectedProfileId(profileParam);
    void setProfileParam(null);
  }, [profileParam, setProfileParam]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [rejectModal, setRejectModal] = useState<{
    selection: PipelineActionBody;
    selectionCount: number;
    fallback: { type: "archive" } | { type: "move"; stageId: string };
  } | null>(null);
  const [bulkMoveSession, setBulkMoveSession] =
    useState<BulkMoveProgressSession | null>(null);
  const queryClient = useQueryClient();

  const archiveStage = useMemo(() => getArchiveStage(stages), [stages]);
  const {
    isLinked,
    connectionId,
    connectorName: ats,
    capabilitiesSnapshot: snapshot,
    dormant,
  } = useJobAtsLink(job.id);

  // Show the ATS "Sync" column only while the job is actively linked. A
  // dormant (unlinked) job reads "No External ATS" in its header, so a column
  // of paused sync states is noise; it returns on relink.
  const showSyncColumn = isLinked;

  const profileIds = useMemo(
    () => candidates.map((c) => c.profile_id),
    [candidates],
  );

  const handlePipelineError = useCallback(
    (err: unknown, fallbackMsg: string, action?: "move" | "archive") => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
        return;
      }
      const description =
        action != null
          ? descriptionForPipelineBulkError(err, action)
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred.";
      showToast.error(fallbackMsg, { description });
    },
    [],
  );

  const bulkMoveMutation = useMutation({
    mutationFn: ({
      candidateIds,
      selection,
      stageId,
    }: {
      candidateIds?: string[];
      selection?: PipelineActionBody;
      stageId: string;
    }) => {
      // Explicit-id tray/regular moves ship ids directly. Linked archive-stage
      // moves pass their captured selection, including compact select-all
      // descriptors, without materializing candidate ids.
      if (candidateIds) {
        return bulkMove(job.id, {
          job_candidate_ids: candidateIds,
          target_stage_id: stageId,
        });
      }
      if (selection) {
        return bulkMove(job.id, { ...selection, target_stage_id: stageId });
      }
      const body = buildPipelineActionBody();
      if (!body) throw new Error("No candidates selected");
      return bulkMove(job.id, { ...body, target_stage_id: stageId });
    },
    onSuccess: (res, { candidateIds }) => {
      // Async bulk move (≥2): stages already committed; poll job via progress
      // toast. Skip optimistic queued patch — SSE from enqueue is authoritative.
      if (res.bulk_move_job_id) {
        setBulkMoveSession({
          jobId: job.id,
          bulkMoveJobId: res.bulk_move_job_id,
          movedCount: res.updated,
          toastId: `bulk-move-progress:${res.bulk_move_job_id}`,
        });
      } else {
        // Single-id (or sync) path: immediate success toast.
        showToast.success(
          `Moved ${res.updated} ${res.updated === 1 ? "profile" : "profiles"}`,
        );
        // Only the explicit-id path can optimistically patch the moved rows;
        // the descriptor path doesn't know the concrete ids client-side.
        if (candidateIds) {
          patchSyncedCandidatesQueued(
            queryClient,
            job.id,
            candidateIds,
            "stage_change",
          );
        }
      }
      clearSelection();
      onRefresh();
      invalidateSyncPreflight(queryClient, job.id);
    },
    onError: (err) => {
      handlePipelineError(err, "Failed to move candidates", "move");
      // No optimistic patch on failure — refresh so any stale UI settles.
      onRefresh();
    },
  });

  const handleBulkMoveProgressFinished = useCallback(
    (bulkMoveJobId: string) => {
      setBulkMoveSession((prev) =>
        prev?.bulkMoveJobId === bulkMoveJobId ? null : prev,
      );
    },
    [],
  );

  const bulkArchiveMutation = useMutation({
    mutationFn: ({
      candidateIds,
      selection,
    }: {
      candidateIds?: string[];
      selection?: PipelineActionBody;
    }) => {
      // Explicit-id unlinked archives ship ids directly. Linked fallbacks pass
      // their captured selection, while select-all remains compact.
      if (candidateIds) {
        return bulkArchive(job.id, { job_candidate_ids: candidateIds });
      }
      if (selection) {
        return bulkArchive(job.id, selection);
      }
      const body = buildPipelineActionBody();
      if (!body) throw new Error("No candidates selected");
      return bulkArchive(job.id, body);
    },
    onSuccess: (data, { candidateIds }) => {
      // Local archive path (unlinked job or reject-unavailable fallback): the
      // ATS reject modal does not own the result toast.
      const count = candidateIds ? candidateIds.length : data.updated;
      showToast.success(
        `Archived ${count} ${count === 1 ? "profile" : "profiles"}`,
      );
      clearSelection();
      onRefresh();
      invalidateSyncPreflight(queryClient, job.id);
    },
    onError: (err) =>
      handlePipelineError(err, "Failed to archive candidates", "archive"),
  });

  const handleMoveToStage = useCallback(
    async (stageId: string) => {
      // Linked moves to Archived pass one selection to bulk reject; the backend
      // owns the pushed/unpushed split. Unsupported reject falls back locally.
      if (
        archiveStage &&
        stageId === archiveStage.id &&
        isLinked &&
        connectionId
      ) {
        const body = buildPipelineActionBody();
        if (!body) return;
        if (!rejectAvailability(snapshot, ats, true).allowed) {
          await bulkMoveMutation.mutateAsync({ selection: body, stageId });
        } else {
          setRejectModal({
            selection: body,
            selectionCount: getSelectedCount(),
            fallback: { type: "move", stageId },
          });
        }
        return;
      }

      // Regular stage move: never gates. Include mode ships the explicit ids
      // (so onSuccess can optimistically patch the moved rows); all mode ships
      // the compact descriptor the backend resolves. Any ATS auto-sync outcome
      // is surfaced by bulkMoveMutation.onSuccess.
      if (bulkMode === "all") {
        if (getSelectedCount() === 0) return;
        await bulkMoveMutation.mutateAsync({ stageId });
        return;
      }
      const ids = Array.from(selectedCandidateIds);
      if (ids.length === 0) return;
      await bulkMoveMutation.mutateAsync({ candidateIds: ids, stageId });
    },
    [
      archiveStage,
      isLinked,
      connectionId,
      snapshot,
      ats,
      bulkMoveMutation,
      bulkMode,
      selectedCandidateIds,
      getSelectedCount,
    ],
  );

  const handleArchive = useCallback(async () => {
    // Linked archive passes one selection to bulk reject; the backend owns the
    // pushed/unpushed split. Unsupported reject falls back to local archive.
    if (isLinked && connectionId) {
      const body = buildPipelineActionBody();
      if (!body) return;
      if (!rejectAvailability(snapshot, ats, true).allowed) {
        await bulkArchiveMutation
          .mutateAsync({ selection: body })
          .catch(() => {});
      } else {
        setRejectModal({
          selection: body,
          selectionCount: getSelectedCount(),
          fallback: { type: "archive" },
        });
      }
      return;
    }

    // Unlinked job: include mode archives the explicit ids; all mode ships the
    // descriptor so the backend resolves the set.
    if (bulkMode === "all") {
      if (getSelectedCount() === 0) return;
      await bulkArchiveMutation.mutateAsync({});
      return;
    }
    const ids = Array.from(selectedCandidateIds);
    if (ids.length === 0) return;
    await bulkArchiveMutation.mutateAsync({ candidateIds: ids });
  }, [
    isLinked,
    connectionId,
    snapshot,
    ats,
    bulkArchiveMutation,
    bulkMode,
    selectedCandidateIds,
    getSelectedCount,
  ]);

  // Needs-stage tray row action: reuses the same move mutation as the rest
  // of the pipeline (auto-sync toast + refresh on success) for a single
  // candidate — bulk_move clears held_state server-side, so a plain move
  // resolves the row.
  const handleTrayMoveToStage = useCallback(
    async (candidateId: string, stageId: string) => {
      await bulkMoveMutation.mutateAsync({
        candidateIds: [candidateId],
        stageId,
      });
    },
    [bulkMoveMutation],
  );

  const handleCandidateClick = useCallback((candidate: PipelineCandidate) => {
    setSelectedProfileId(candidate.profile_id);
  }, []);

  const handleSelectAllCandidates = useCallback(() => {
    // Virtual select-all: store the count + active filters; the backend
    // resolves the actual candidates when an action fires. No fetch.
    selectAllCandidates(totalCount);
  }, [selectAllCandidates, totalCount]);

  const exportScope = pipelineExportScope.all(job.id);
  const exporting = useExportBusy(exportScope);
  const startExportJob = useStartExportJob();

  // Export every candidate matching the active filters via a compact `all`
  // descriptor — the backend resolves the set, so the estimate + export never
  // need the full ID list on the client. The filter panel's selections ride
  // along so an export can never cover more candidates than the table showed.
  const exportSource = useMemo<ExportSource | null>(
    () =>
      totalCount > 0
        ? {
            type: "pipeline",
            job_id: job.id,
            bulk: { mode: "all", exclude_ids: [] },
            stage_id: selectedStageId ?? undefined,
            search: filterSearch || undefined,
            filters: filters ?? undefined,
          }
        : null,
    [totalCount, job.id, selectedStageId, filterSearch, filters],
  );

  const handleToolbarExport = useCallback(
    (
      format: ExportFormat,
      includeContacts: boolean,
      options: ExportOptions,
    ) => {
      if (!exportSource) return;
      void startExportJob({
        scope: exportScope,
        label: profileExportLabel(totalCount, format),
        format,
        adapter: profileExportAdapter,
        reducible: true,
        post: () =>
          requestExport({
            source: exportSource,
            format,
            include_contacts: includeContacts,
            options,
          }),
      });
    },
    [exportScope, exportSource, startExportJob, totalCount],
  );

  const isEmpty = !loading && candidates.length === 0;

  // One-time guided tour once the pipeline table has candidates on screen.
  useAutoStartTour("pipeline", !loading && candidates.length > 0);

  return (
    // The stage bar spans the full width; below it, the filter rail sits left of
    // the toolbar + table so its header lines up with the search row. The rail
    // is collapsed to an icon strip by default. `min-w-0` on the table column
    // keeps the table's horizontal scroll inside it rather than pushing the rail
    // off screen.
    <div className="flex flex-1 flex-col overflow-hidden">
      <StageBar />
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <PipelineFilterSidebar
        mobileOpen={mobileFiltersOpen}
        onMobileOpenChange={setMobileFiltersOpen}
      />
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
      <PipelineToolbar
        totalCount={totalCount}
        onOpenViewOptions={() => setViewOptionsOpen(true)}
        onOpenExport={() => setExportModalOpen(true)}
        exporting={exporting}
        candidates={candidates}
        onSelectAllCandidates={handleSelectAllCandidates}
        atsAction={<AtsToolbarAction jobId={job.id} />}
        filterAction={
          <PipelineFilterButton onClick={() => setMobileFiltersOpen(true)} />
        }
      />
      <div className="flex flex-col flex-1 min-h-0">
        {isEmpty ? (
          <PipelineEmptyState
            onAddProfiles={hasActiveFilters ? undefined : onOpenImport}
            filtered={hasActiveFilters}
          />
        ) : (
          <>
            <NeedsStageTray
              jobId={job.id}
              candidates={candidates}
              onMoveToStage={handleTrayMoveToStage}
            />
            <div
              data-tour="pipe-table"
              className="relative hidden md:flex flex-col flex-1 min-h-0"
            >
              <PipelineTable
                candidates={candidates}
                stages={stages}
                totalCount={totalCount}
                loading={loading}
                onCandidateClick={handleCandidateClick}
                onSelectAllCandidates={handleSelectAllCandidates}
                showSyncColumn={showSyncColumn}
              />
              <AnimatePresence>
                {hasSelection && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="pointer-events-none absolute inset-x-0 bottom-[67px] z-[35] h-[260px]"
                    style={{
                      background:
                        "linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--surface-primary) 85%, transparent) 60%, var(--surface-primary) 100%)",
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
            <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:hidden bg-surface-primary">
              <MobilePipelineCards
                candidates={candidates}
                stages={stages}
                totalCount={totalCount}
                loading={loading}
                onCandidateClick={handleCandidateClick}
                onSelectAllCandidates={handleSelectAllCandidates}
              />
            </div>
          </>
        )}
      </div>
      <PipelineBulkActions
        candidates={candidates}
        onMoveToStage={handleMoveToStage}
        onArchive={handleArchive}
        onDeselect={clearSelection}
      />
      <PipelineProfile
        profileId={selectedProfileId}
        profileIds={profileIds}
        open={selectedProfileId !== null}
        onClose={() => setSelectedProfileId(null)}
        onNavigate={setSelectedProfileId}
        candidates={candidates}
        selectedStageId={selectedStageId}
      />
      <PipelineViewOptions
        open={viewOptionsOpen}
        onClose={() => setViewOptionsOpen(false)}
        hiddenColumns={showSyncColumn ? undefined : ["sync"]}
      />
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        profileCount={totalCount}
        source={exportSource}
        onExport={handleToolbarExport}
      />
      {isLinked && connectionId && rejectModal && (
        <RejectInAtsModal
          open
          onOpenChange={(o) => !o && setRejectModal(null)}
          connectionId={connectionId}
          selection={rejectModal.selection}
          jobId={job.id}
          selectionCount={rejectModal.selectionCount}
          snapshot={snapshot}
          ats={ats}
          onRejectUnavailable={async () => {
            if (rejectModal.fallback.type === "archive") {
              await bulkArchiveMutation.mutateAsync({
                selection: rejectModal.selection,
              });
            } else {
              await bulkMoveMutation.mutateAsync({
                selection: rejectModal.selection,
                stageId: rejectModal.fallback.stageId,
              });
            }
          }}
          onRejected={() => {
            setRejectModal(null);
            clearSelection();
            onRefresh();
          }}
        />
      )}
      {bulkMoveSession && (
        <BulkMoveProgressToast
          key={bulkMoveSession.bulkMoveJobId}
          session={bulkMoveSession}
          onFinished={handleBulkMoveProgressFinished}
        />
      )}
      </div>
      </div>
    </div>
  );
}
